import { Matrix4, Quaternion, Vector3 } from 'three';
/**
 * Model in a 3D scene.
 */
export class Model3D {
    /**
     * Create a new 3D model.
     *
     * @param object
     */
    constructor(object) {
        Object.defineProperty(this, "object", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "position", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "rotation", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "scale", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "positionDefault", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.object = object;
        this.position = new Vector3();
        this.rotation = new Quaternion();
        this.scale = new Vector3(1, 1, 1);
        this.positionDefault = new Vector3();
        this.object.getEstimatedCenter(this.positionDefault);
        // Set the initial transform
        this.position.copy(this.positionDefault);
    }
    getWorldObject() {
        return this.object;
    }
    async hide() {
        this.object.setVisible(false);
        return this;
    }
    async show() {
        this.object.setVisible(true);
        return this;
    }
    async destroy() {
        await this.hide();
        this.object.dispose();
    }
    isVisible() {
        return this.object.getVisible() && this.getOpacity() > 0;
    }
    isPicked(pick) {
        return pick.object === this.object;
    }
    getBoundingRadius() {
        return this.object.getBoundingVolume()[3];
    }
    getPosition(position) {
        if (!position)
            position = new Vector3();
        return position.copy(this.position);
    }
    getRotation(rotation) {
        if (!rotation)
            rotation = new Quaternion();
        return rotation.copy(this.rotation);
    }
    getScale(scale) {
        if (!scale)
            scale = new Vector3(1, 1, 1);
        return scale.copy(this.scale);
    }
    setPosition(position) {
        this.position.copy(position);
        this.updateTransform();
    }
    setRotation(rotation) {
        this.rotation.copy(rotation);
        this.updateTransform();
    }
    setScale(scale) {
        this.scale.copy(scale);
        this.updateTransform();
    }
    /**
     * Get the default position of the Model3D.
     *
     * This is useful if the position is geolocated.
     *
     * @param position
     * @returns
     */
    getPositionDefault(position) {
        if (!position)
            position = new Vector3();
        return position.copy(this.positionDefault);
    }
    /**
     * Set the opacity of the model.
     *
     * @param opacity
     */
    setOpacity(opacity) {
        const blendable = this.object.asBlendableObject();
        if (blendable) {
            blendable.opacity = opacity;
        }
    }
    /**
     * Get the opacity of the model.
     *
     * @returns
     */
    getOpacity() {
        const blendable = this.object.asBlendableObject();
        return blendable?.opacity ?? 1;
    }
    /**
     * Update the transform of the underlying 3D world object.
     *
     * This is tricky to do because the origin can be the center of the globe.
     *
     * This assumes that the world object is in its default, initial position.
     */
    updateTransform() {
        // Move the mesh to the origin
        const modelSpaceMatrix = new Matrix4();
        modelSpaceMatrix.makeTranslation(-this.positionDefault.x, -this.positionDefault.y, -this.positionDefault.z);
        // Scale the mesh
        const scaleMatrix = new Matrix4();
        scaleMatrix.makeScale(this.scale.x, this.scale.y, this.scale.z);
        // Rotate the mesh in-place
        const rotationMatrix = new Matrix4();
        const quaternion = new Quaternion(this.rotation.x, this.rotation.y, this.rotation.z, this.rotation.w);
        rotationMatrix.makeRotationFromQuaternion(quaternion);
        // Move the mesh to the desired position
        const translationMatrix = new Matrix4();
        translationMatrix.makeTranslation(this.position.x, this.position.y, this.position.z);
        // Compute the final transform
        const transform = translationMatrix
            .multiply(rotationMatrix)
            .multiply(scaleMatrix)
            .multiply(modelSpaceMatrix);
        // Decompose the transform into position, rotation, and scale
        const objPosition = new Vector3();
        const objRotation = new Quaternion();
        const objScale = new Vector3();
        transform.decompose(objPosition, objRotation, objScale);
        // Update the object
        this.object.setPositionECEF(objPosition);
        this.object.setRotation(objRotation);
        this.object.asScalableObject()?.setScale(objScale);
    }
}
